import {
  addDays,
  addHours,
  addMonths,
  addWeeks,
  differenceInDays,
  differenceInMinutes,
  differenceInMonths,
  eachDay,
  format,
  isAfter,
  isBefore,
  isThisMinute,
  isToday,
  isWithinRange,
  lastDayOfMonth,
  startOfMonth,
  startOfWeek,
  subDays,
} from 'date-fns'
// import idLocale from 'date-fns/locale/id'
import enLocale from 'date-fns/locale/en'

import { toInteger } from '@/utils/numberUtils'

export enum Week {
  Monday = 1,
  Tuesday = 2,
  Wednesday = 3,
  Thursday = 4,
  Friday = 5,
  Saturday = 6,
  Sunday = 7,
}

export enum ShortWeek {
  Mon = 1,
  Tue = 2,
  Wed = 3,
  Thu = 4,
  Fri = 5,
  Sat = 6,
  Sun = 7,
}

export const getLastDayOfMonth = (date: Date | string | number = new Date()) => {
  return lastDayOfMonth(date)
}

export const getTomorrow = (date: Date = new Date()) => {
  return addDays(date, 1)
}

export const getTodayOfNextWeek = (date: Date = new Date()) => {
  return addWeeks(date, 1)
}

export const getTodayOfNextMonth = (date: Date = new Date()) => {
  return addMonths(date, 1)
}

export const getFormattedDate = (
  date: Date | string | number = new Date(),
  givenFormat: string = 'YYYY.MM.DD',
  options: {
    locale?: Object
  } = { locale: enLocale },
) => {
  return format(date, givenFormat, options)
}

export const getFormattedDateByJakarta = (
  date: Date | string | number = new Date(),
  givenFormat: string = 'YYYY.MM.DD',
  options: {
    locale?: Object
  } = { locale: enLocale },
) => {
  return getFormattedDate(getJakartaDate(date), givenFormat, options)
}

export const getIsThisMinute = isThisMinute

export const getIsWithin1Hour = (timestamp: number, now: Date = new Date()) => {
  const oneHourBefore = addHours(now, -1)

  return isWithinRange(new Date(timestamp), oneHourBefore, now)
}

export const getIsToday = isToday

export const getIsBefore = isBefore

export const getIsAfter = isAfter

export const getDifferenceInMinutes = differenceInMinutes

export const getDays = (startDate: Date = new Date(), range: number) => {
  return eachDay(startDate, addDays(startDate, range))
}

export const getStartDateOfThisMonth = () => {
  return startOfMonth(new Date())
}

export const getStartDateOfThisWeek = (weekStartsOn: Week = Week.Monday) => {
  return startOfWeek(new Date(), { weekStartsOn })
}

export const getDayOfWeekNumber = (date: Date = new Date()) => {
  // 월: 1 ~ 일: 7
  return toInteger(getFormattedDate(date, 'd'))
}

export const getDayOfMonth = (date: Date = new Date()) => {
  return getFormattedDate(date, 'Do')
}

export const getDayOfMonthNumber = (date: Date = new Date()) => {
  return toInteger(getFormattedDate(date, 'DD'))
}

export const getDifferenceInDays = (from: Date | string, to: Date | string) => {
  if (typeof from === 'string' || typeof to === 'string') {
    return differenceInDays(to, from)
  }

  return differenceInDays(getFormattedDate(to, 'YYYY-MM-DD'), getFormattedDate(from, 'YYYY-MM-DD'))
}

export const getDifferenceInMonths = (from: Date, to: Date) => {
  return differenceInMonths(from, to)
}

export const getMonthName = (
  date: Date | string | number = new Date(),
  givenFormat: string = 'MMMM',
  options: {
    locale?: Object
  } = { locale: enLocale },
) => {
  return format(date, givenFormat, options)
}

export const getJakartaDate = (date: string | number | Date = new Date()): Date => {
  const milliseconds =
    typeof date === 'string'
      ? new Date(date).getTime()
      : date instanceof Date
      ? date.getTime()
      : date
  const jakartaHourOffset = 7
  const offset = jakartaHourOffset * 60 * 60 * 1000
  const dateString = new Date(milliseconds + offset).toISOString()
  const splitedDate: number[] = dateString.split(/[^0-9]/).map(item => parseInt(item))

  // [Warning]
  // https://git-dev.linecorp.com/Garuda/garuda_webview/pull/792
  return new Date(
    splitedDate[0], // Year
    splitedDate[1] - 1, // Month
    splitedDate[2], // Date
    splitedDate[3], // Hour
    splitedDate[4], // Minutes
    splitedDate[5], // Seconds
  )
}

export const getCancellableLastDate = (date: number) => {
  return subDays(date, 1)
}

export const getWeekdayRange = (startDate: string, endDate: string) => {
  return eachDay(startDate, endDate)
    .reduce(
      (list: number[], date) => (list.includes(date.getDay()) ? list : [...list, date.getDay()]),
      [],
    )
    .sort()
}
